/**
 * Feedback Environment Context
 *
 * Builds the environment footer appended to feedback messages so support can
 * triage without a back-and-forth (app version, OS, engines in use). Travels
 * inside the message text — the backend contract stays untouched.
 *
 * PRIVACY: the footer may contain ONLY what the in-dialog disclosure and the
 * privacy policy cover — app version, platform/arch, OS version, and database
 * engine identifiers. Session/workspace data (tab info, session duration,
 * locale) must NOT be added here: the privacy policy (§2.5 Local Data)
 * promises session data is never transmitted. Collection reuses the
 * privacy-vetted crash-dialog snapshot collectors.
 */

import {
  collectAppInfo,
  collectConnectionSnapshot,
} from '../../crash-dialog/services/crashContextCollector';

export type FeedbackEnvironment = {
  appVersion: string;
  platform: string;
  arch: string;
  /** Human-readable OS version, e.g. "macOS 26.0" / "Windows 11", or null when undetectable. */
  osVersion: string | null;
  /** Engines of active connections, deduped, e.g. ['mssql', 'pgsql']. */
  engines: string[];
};

/**
 * Map a UA-Client-Hints `platformVersion` to a human-readable OS version.
 * Windows reports an abstract version: major >= 13 means Windows 11, 1-12
 * means Windows 10, and 0 means an older Windows that hides its version.
 * On darwin/linux a 0 major means a frozen/reduced UA-CH value — treat it
 * as unknown rather than claiming "macOS 0.0".
 */
export function formatOsVersion(platform: string, platformVersion: string): string | null {
  if (!platformVersion) return null;
  const parts = platformVersion.split('.');
  const major = Number.parseInt(parts[0], 10);
  if (Number.isNaN(major)) return null;
  const majorMinor = parts.slice(0, 2).join('.');

  if (platform === 'win32') {
    if (major >= 13) return 'Windows 11';
    if (major >= 1) return 'Windows 10';
    return 'Windows';
  }
  if (major === 0) return null;
  if (platform === 'darwin') return `macOS ${majorMinor}`;
  if (platform === 'linux') return `Linux ${majorMinor}`;
  return platformVersion;
}

/**
 * Render the footer appended to the feedback message. Always emits the app
 * line (platform alone is often the answer — see the localhost\SQLEXPRESS
 * report); the engines line only when something is connected.
 */
export function buildEnvironmentFooter(env: FeedbackEnvironment): string {
  const appSegments = [`App ${env.appVersion}`, `${env.platform}/${env.arch}`];
  if (env.osVersion) appSegments.push(env.osVersion);

  const lines = [appSegments.join(' · ')];
  if (env.engines.length > 0) lines.push(`Engines: ${env.engines.join(', ')}`);
  return `\n\n—\n${lines.join('\n')}`;
}

type UserAgentDataWithHints = {
  getHighEntropyValues?: (hints: string[]) => Promise<{ platformVersion?: string }>;
};

async function detectOsVersion(platform: string): Promise<string | null> {
  try {
    const uaData = (navigator as Navigator & { userAgentData?: UserAgentDataWithHints })
      .userAgentData;
    if (!uaData?.getHighEntropyValues) return null;
    const { platformVersion } = await uaData.getHighEntropyValues(['platformVersion']);
    return formatOsVersion(platform, platformVersion ?? '');
  } catch {
    return null;
  }
}

/**
 * Gather the environment snapshot for a feedback submission. Every part is
 * best-effort — a failing collector degrades to its safe default rather than
 * blocking the submission.
 */
export async function collectFeedbackEnvironment(): Promise<FeedbackEnvironment> {
  const appInfo = await collectAppInfo();
  const connection = collectConnectionSnapshot();

  return {
    appVersion: appInfo.version,
    platform: appInfo.platform,
    arch: appInfo.arch,
    osVersion: await detectOsVersion(appInfo.platform),
    engines: connection.connectionEngines,
  };
}
